import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import MainLayout from '../layouts/MainLayout';
import Button from '../components/ui/Button';
import Input from '../components/ui/Input';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/Table';
import GroupForm from '../components/GroupForm';
import { apiService } from '../services/api';
import { toast } from 'react-toastify';

interface Group {
  id: number;
  title: string;
  position: number;
  published: number;
  created: string;
  updated: string;
  created_user_id?: number;
  updated_user_id?: number;
}

interface Module {
  id: number;
  title: string;
  route: string;
}

interface Permission {
  module_id: number;
  module_name: string;
  route: string;
  can_add: boolean;
  can_edit: boolean;
  can_delete: boolean;
  can_print: boolean;
  can_export: boolean;
  source?: string;
}

interface PaginationInfo {
  current_page: number;
  per_page: number;
  total: number;
  last_page: number;
  from: number;
  to: number;
}

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

interface GroupsResponse {
  items: Group[];
  pagination: PaginationInfo;
}

const Groups: React.FC = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingGroup, setEditingGroup] = useState<Group | null>(null);
  const [formLoading, setFormLoading] = useState(false);
  const { token, hasPermission } = useAuth();
  const navigate = useNavigate();

  const fetchGroups = async (page = 1, searchTerm = '') => {
    setLoading(true);
    try {
      const params: Record<string, any> = { page };
      if (searchTerm) {
        params.search = searchTerm;
      }

      const response = await apiService.get<{ items: Group[]; pagination: PaginationInfo }>('groups', params);
      if (response.success) {
        setGroups(response.data.items);
        setPagination(response.data.pagination);
      }
    } catch (error) {
      console.error('Error fetching groups:', error);
      toast.error('Error fetching groups. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) {
      fetchGroups(currentPage, search);
    }
  }, [token, currentPage]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchGroups(1, search);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleExport = async () => {
    if (!hasPermission('groups', 'export')) {
      toast.error('You do not have permission to export groups.');
      return;
    }

    try {
      const params: Record<string, any> = {};
      if (search) {
        params.search = search;
      }

      await apiService.downloadFile(`groups/export?${new URLSearchParams(params)}`, `groups-${new Date().toISOString().split('T')[0]}.csv`);
    } catch (error) {
      console.error('Error exporting groups:', error);
      toast.error('Error exporting groups. Please try again.');
    }
  };

  const handlePrint = () => {
    if (!hasPermission('groups', 'print')) {
      toast.error('You do not have permission to print groups.');
      return;
    }
    
    const printWindow = window.open('', '_blank');
    if (printWindow) {
      const tableElement = document.querySelector('.groups-table');
      if (tableElement) {
        printWindow.document.write(`
          <html>
            <head>
              <title>Groups Report</title>
              <style>
                body { font-family: Arial, sans-serif; margin: 20px; }
                table { width: 100%; border-collapse: collapse; margin-top: 20px; }
                th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
                th { background-color: #f2f2f2; }
                .header { text-align: center; margin-bottom: 20px; }
              </style>
            </head>
            <body>
              <div class="header">
                <h1>Groups Report</h1>
                <p>Generated on: ${new Date().toLocaleDateString()}</p>
              </div>
              ${tableElement.outerHTML}
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    }
  };

  const handleAddGroup = () => {
    if (!hasPermission('groups', 'add')) {
      toast.error('You do not have permission to add groups.');
      return;
    }
    setEditingGroup(null);
    setShowForm(true);
  };

  const handleEditGroup = (groupId: number) => {
    if (!hasPermission('groups', 'edit')) {
      toast.error('You do not have permission to edit groups.');
      return;
    }
    const group = groups.find(g => g.id === groupId);
    if (group) {
      setEditingGroup(group);
      setShowForm(true);
    }
  };

  const handleDeleteGroup = async (groupId: number) => {
    if (!hasPermission('groups', 'delete')) {
      toast.error('You do not have permission to delete groups.');
      return;
    }
    
    if (window.confirm('Are you sure you want to delete this group?')) {
      try {
        const response = await apiService.delete(`groups/${groupId}`);
        if (response.success) {
          toast.success('Group deleted successfully');
          fetchGroups(currentPage, search);
        }
      } catch (error) {
        console.error('Error deleting group:', error);
        toast.error('Error deleting group. Please try again.');
      }
    }
  };

  const handleSubmitGroup = async (groupData: Omit<Group, 'id'> & { id?: number }) => {
    setFormLoading(true);
    try {
      if (editingGroup) {
        const response = await apiService.put(`groups/${editingGroup.id}`, groupData);
        if (response.success) {
          toast.success('Group updated successfully');
        }
      } else {
        const response = await apiService.post('groups', groupData);
        if (response.success) {
          toast.success('Group created successfully');
        }
      }
      setShowForm(false);
      setEditingGroup(null);
      fetchGroups(currentPage, search);
    } catch (error) {
      console.error('Error saving group:', error);
      toast.error('Error saving group. Please try again.');
    } finally {
      setFormLoading(false);
    }
  };

  const handleCancelForm = () => {
    setShowForm(false);
    setEditingGroup(null);
  };

  const handlePermissionsClick = (group: Group) => {
    navigate(`/permissions/group/${group.id}`, { 
      state: { 
        group,
        type: 'group'
      } 
    });
  };

  return (
    <MainLayout>
      <div className="p-6">
        {/* Page Header */}
        <div className="mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Group Management
              </h1>
              <p className="text-gray-600 dark:text-gray-400">
                Manage system groups and their permissions
              </p>
            </div>
            {hasPermission('groups', 'add') && (
              <Button onClick={handleAddGroup}>
                Add New Group
              </Button>
            )}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="mb-6">
          <div className="flex gap-4">
            <div className="flex-1">
              <Input
                type="text"
                placeholder="Search groups..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Groups Table */}
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden groups-table">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>ID</TableHead>
                <TableHead>Title</TableHead>
                <TableHead>Position</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : groups.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} className="text-center py-8">
                    No groups found
                  </TableCell>
                </TableRow>
              ) : (
                groups.map((group) => (
                  <TableRow key={group.id}>
                    <TableCell>{group.id}</TableCell>
                    <TableCell>{group.title}</TableCell>
                    <TableCell>{group.position}</TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        group.published ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {group.published ? 'Active' : 'Inactive'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        {hasPermission('groups', 'edit') && (
                          <Button
                            size="sm"
                            onClick={() => handleEditGroup(group.id)}
                            className="bg-blue-500 hover:bg-blue-600"
                          >
                            Edit
                          </Button>
                        )}
                        {hasPermission('groups', 'delete') && (
                          <Button
                            size="sm"
                            onClick={() => handleDeleteGroup(group.id)}
                            className="bg-red-500 hover:bg-red-600"
                          >
                            Delete
                          </Button>
                        )}
                        <Button
                          size="sm"
                          onClick={() => handlePermissionsClick(group)}
                          className="bg-green-500 hover:bg-green-600"
                        >
                          Permissions
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>

        {/* Pagination */}
        {pagination && (
          <div className="bg-white dark:bg-gray-800 shadow rounded-lg p-6 mt-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700 dark:text-gray-300">
                Showing {pagination.from} to {pagination.to} of {pagination.total} results
              </div>
              <div className="flex gap-2">
                <Button
                  onClick={() => handlePageChange(pagination.current_page - 1)}
                  disabled={pagination.current_page === 1}
                >
                  Previous
                </Button>
                <span className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300">
                  Page {pagination.current_page} of {pagination.last_page}
                </span>
                <Button
                  onClick={() => handlePageChange(pagination.current_page + 1)}
                  disabled={pagination.current_page === pagination.last_page}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Group Form Modal */}
        {showForm && (
          <GroupForm
            group={editingGroup}
            onSubmit={handleSubmitGroup}
            onCancel={handleCancelForm}
            loading={formLoading}
          />
        )}
      </div>
    </MainLayout>
  );
};

export default Groups; 